
import { defineComponent, onUnmounted, ref, useContext, useFetch, useRoute, watch } from '@nuxtjs/composition-api'
import LazyHydrate from 'vue-lazy-hydration'
import { CacheTag, CacheTagPrefix, useCache } from '@zaprooecommerce/vsf-cache'
import { isEmpty, merge, sortBy, unescape } from 'lodash-es'
import { CustomizationOption, Product } from '~/modules/catalog/product/types'
import { useCart, UseCartAddItemParams, useMagentoConfiguration, useProduct, useWishlist } from '~/composables'
import { getGallery, getName, getPrice, getBreadcrumbs } from '~/modules/catalog/product/getters/productGetters'
import { getLoyaltyPrice } from '~/modules/catalog/aatrium/getters/productGetters'
import { getTotals } from '~/modules/checkout/getters/cartGetters'
import { useLastViewedProductsStore } from '~/modules/last-viewed-products/store/lastViewedProductsStore'
import {
  MediaGalleryItem, PICKUP_MACHINE_VALUES, ProductGallery, Transport, Breadcrumb, AatriumProduct
} from '~/modules/catalog/types'
import { MetaBuilder } from '~/helpers/meta/MetaBuilder'
import { safeComputed } from '~/modules/helpers'
import {
  useRelatedProducts as useAatriumRelatedProducts
} from '~/modules/catalog/aatrium/product/composables/useRelatedProducts'
import { sendGtagItemListEvent, sendGtagProductEvent } from '~/modules/aatrium-gtm/events'
import { useI18n } from '~/helpers/hooks/usei18n'
import { ProductsType } from '~/modules/catalog/aatrium/variables/products'
import { useProductSensitive } from '~/modules/catalog/aatrium/product/composables/useProductSensitive'
import ImageGallery from '~/components/molecules/ImageGallery.vue'
import AddToWishList from '~/components/molecules/AddToWishList.vue'
import ProductBadges from '~/components/ProductBadges/ProductBadges.vue'
import ProductPrice from '~/modules/catalog/aatrium/components/ProductPrice.vue'
import NotFoundPageWrapper from '~/modules/not-found-page-wrapper/components/NotFoundPageWrapper.vue'
import ProductAvailability from '~/modules/catalog/aatrium/components/ProductAvailability.vue'
import { GtagItemListActions, GtagProductActions } from '~/modules/aatrium-gtm'
import { preparedGtagItems } from '~/modules/aatrium-gtm/helpers'
import { useRedirectToCorrectUrl } from '~/modules/product-correct-url/composables'
import { useProductAnalyticsStore } from '~/modules/catalog/aatrium/product/store/useProductAnalyticsStore'
import Breadcrumbs from '~/components/molecules/Breadcrumbs.vue'
import Tabs from '~/components/organisms/Tabs/Tabs.vue'
import Tab from '~/components/organisms/Tabs/Tab.vue'
import { useStringifyHtml } from '~/composables/useStringifyHtml'
import { META_DESCRIPTION_MAX_LENGTH } from '~/helpers/meta/variables'
import PageLazyHydrate from '~/components/PageLazyHedrate.vue'

export default defineComponent({
  name: 'ProductPage',
  components: {
    PageLazyHydrate,
    Breadcrumbs,
    ImageGallery,
    ProductPrice,
    ProductBadges,
    GiftCardProduct: () => import('~/modules/catalog/aatrium/components/GiftCardProduct.vue'),
    CustomizationAreas: () => import('~/components/CustomizationAreas/CustomizationAreas.vue'),
    LoyaltyPrice: () => import('~/modules/catalog/aatrium/components/LoyaltyPrice.vue'),
    Tabs,
    Tab,
    LastViewedProducts: () => import('~/modules/last-viewed-products/components/LastViewedProducts.vue'),
    InstallmentCalculator: () => import('~/components/InstallmentCalculator.vue'),
    Heading: () => import('~/components/atoms/Heading.vue'),
    Icon: () => import('~/components/atoms/Icon.vue'),
    QuantitySelector: () => import('~/components/QuantitySelector.vue'),
    AddToCart: () => import('~/components/molecules/AddToCart.vue'),
    PackageList: () => import('~/components/molecules/PackageList.vue'),
    // ParametersList: () => import("~/components/molecules/ParametersList.vue"),
    ProductsCarouselWrapper: () => import('~/components/organisms/ProductsCarouselWrapper.vue'),
    ProductPageSkeleton: () => import('~/components/skeletons/ProductPageSkeleton.vue'),
    LazyHydrate,
    NotFoundPageWrapper,
    ProductAvailability,
    MicrodataProduct: () => import('~/modules/microdata/components/MicrodataProduct.vue'),
    AddToWishList
  },
  transition: 'fade',
  setup () {
    const product = ref<AatriumProduct | null>(null)
    const { addTags } = useCache()
    const route = useRoute()
    const { getProductDetails, loading } = useProduct()
    const { addItem, cart } = useCart()
    const { isInWishlist, removeItem } = useWishlist()
    const { localePath, $config } = useContext()
    const { params: { id } } = route.value
    const { redirectToCorrectUrl, setRedirectedProduct } = useRedirectToCorrectUrl()
    const { search: searchAatriumRelatedProducts, relatedProducts, loading: relatedProductsLoading } = useAatriumRelatedProducts(id)
    const productAnalyticsStore = useProductAnalyticsStore()
    const { add: addToLastViewedProducts } = useLastViewedProductsStore()
    const { applyProductTierPrices } = useProductSensitive()
    const i18n = useI18n()
    const { selectedCurrency } = useMagentoConfiguration()
    const { stringHtml, stringifyHtml } = useStringifyHtml()
    const { storeURL } = $config
    const qty = ref<number>(1)
    const breadcrumbs = safeComputed<Breadcrumb[]>(() => {
      const productCategories = product.value?.categories.length ? [...product.value.categories] : []
      const productBreadcrumbs = getBreadcrumbs(product.value, productCategories?.pop())
      return [
        { text: i18n.t('Home') as string, link: localePath('/') },
        ...productBreadcrumbs.map(({ link, ...breadcrumb }) => ({ ...breadcrumb, link: localePath(link) }))
      ]
    })

    const productDescription = safeComputed(() => product.value?.description?.html || '')
    const productName = safeComputed(() => product.value?.name || '')
    const metaTitle = safeComputed(() => product.value?.meta_title || product.value?.name)
    const metaProductImage = safeComputed(() => product.value?.image?.url || '')
    const metaDescription = safeComputed(() => product.value?.meta_description || '')
    const productCanBeAdded = safeComputed<boolean>(() => {
      if (!customizationOptions.value.length) {
        return true
      }

      return customizationOptions.value.filter(option => option.required && !option.selectedValue).length === 0
    })

    const isGiftCardProduct = safeComputed(() => product.value && product.value.__typename === ProductsType.GiftCard)
    const unescapedDescription = safeComputed(() => product.value && unescape(product.value.description.html))

    const customizationOptions = ref<CustomizationOption[]>([])

    const dimensionValues = safeComputed(() => {
      const dimensionValues = []

      if (!product.value) {
        return dimensionValues
      }

      const dimensionAttrs = ['width', 'height', 'depth', 'weight']

      dimensionAttrs.forEach((attrName) => {
        if (product.value[attrName]) {
          dimensionValues.push({ label: attrName, value: product.value[attrName] })
        }
      })

      return dimensionValues
    })

    function getBaseSearchQuery () {
      return {
        filter: {
          sku: {
            eq: id
          }
        },
        configurations: Object.entries(route.value.query)
          .filter(config => config[0] !== 'wishlist')
          .map(
            config => config[1]
          ) as string[]
      }
    }

    async function fetchProduct (query = getBaseSearchQuery()) {
      const result = await getProductDetails({
        ...query
      })

      if (result?.items.length) {
        product.value = merge({} as any, product.value, result?.items[0] as Product ?? {})
        customizationOptions.value = product.value?.options?.map((option: CustomizationOption) => {
          return {
            ...option,
            selectedValue: null,
            isOpen: false
          }
        }) || []
      }
    }

    useFetch(async () => {
      await fetchProduct()

      if (Boolean(product?.value?.sku) === false) {
        return
      }

      setRedirectedProduct(product.value)
      if (redirectToCorrectUrl()) {
        return
      }

      await searchAatriumRelatedProducts()
      await stringifyHtml(metaDescription.value || productDescription.value, META_DESCRIPTION_MAX_LENGTH)

      if (relatedProducts.value.length) {
        sendGtagItemListEvent(GtagItemListActions.ViewItemList, {
          items: preparedGtagItems(relatedProducts.value.map(relatedProduct => ({ product: relatedProduct }))),
          item_list_name: 'Related products'
        })
      }

      const tags: CacheTag[] = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${id}`
        }
      ]

      const productTags: CacheTag[] = [{
        prefix: CacheTagPrefix.Product,
        value: product.value.uid
      }]

      addTags([...tags, ...productTags])
    })

    onUnmounted(() => {
      addToLastViewedProducts(product.value)
    })

    const transports = safeComputed<Transport[]>(() => {
      const transportsList: Transport[] = [
        {
          iconName: 'pickup-shop',
          text: `${i18n.t('PICKUP_DELIVERY_TITLE')}, Pärnu mnt 232/9, Tallinn`
        },
        {
          iconName: 'home-delivery',
          text: i18n.t('AATRIUM_HOME_DELIVERY_TITLE')
        },
        {
          iconName: 'courier',
          text: i18n.t('SPECIAL_COURIER_DELIVERY_TITLE')
        }
      ]

      if (product.value?.parcel_machine === PICKUP_MACHINE_VALUES.YES) {
        transportsList.push({
          iconName: 'parcel-machine',
          text: i18n.t('PARCEL_MACHINE_DELIVERY_TITLE')
        })
      }

      return transportsList
    })

    const productDataIsLoading = safeComputed(() => loading.value && !getName(product.value))

    const productImages = safeComputed(() => {
      if (productDataIsLoading.value || isEmpty(product.value)) {
        return null
      }

      const gallery: ProductGallery[] = getGallery(product.value).map((img: MediaGalleryItem) => ({
        mobile: { url: img.small },
        desktop: { url: img.normal },
        big: { url: img.big },
        alt: product.value?.name || 'aatrium_image',
        position: img.position
      }))

      if (!gallery.length) {
        gallery.push({
          mobile: { url: '/productpage/placeholder.webp' },
          desktop: { url: '/productpage/placeholder.webp' },
          big: { url: '/productpage/placeholder.webp' },
          alt: product.value?.name || 'placeholder',
          position: 1
        })
      }

      return sortBy(gallery, ['position'])
    })

    const productPrice = safeComputed(() => !isEmpty(product.value) && getPrice(product.value))
    const productLoyaltyPrice = safeComputed(() => !isEmpty(product.value) && getLoyaltyPrice(product.value))
    const showLoyaltyPrice = safeComputed(() => (
      productLoyaltyPrice.value && productPrice.value.regular > productLoyaltyPrice.value && !productPrice.value.special
    ))
    const productInWishlist = safeComputed(() => !isEmpty(product.value) && isInWishlist({ product: product.value }))

    async function addProductToCart () {
      const typename = product.value.__typename
      const item: UseCartAddItemParams<Product> = {
        product: product.value,
        quantity: qty.value
      }

      if (customizationOptions.value && customizationOptions.value.length) {
        item.entered_options = customizationOptions.value.filter(option => option.selectedValue).map((option) => {
          return {
            uid: option.uid,
            value: option.selectedValue.option_type_id.toString()
          }
        })
      }

      if (typename === 'SimpleProduct') {
        await addItem(item, {
          index: productAnalyticsStore.productId
        })

        if (productInWishlist.value) {
          await removeItem({ product: product.value })
        }
      }
    }

    watch(() => product.value, (newProduct) => {
      if (newProduct) {
        applyProductTierPrices(product)
      }
    }, { immediate: true })

    watch(() => product.value, (newProduct) => {
      if (newProduct) {
        sendGtagProductEvent(GtagProductActions.ViewItem, {
          items: preparedGtagItems([{ product: newProduct, index: productAnalyticsStore.productId }]),
          value: getTotals(cart.value)?.total ?? 0,
          currency: selectedCurrency.value
        })
      }
    })

    return {
      id,
      qty,
      storeURL,
      productName,
      productDescription,
      metaTitle,
      metaDescription,
      metaProductImage,

      breadcrumbs,
      product,
      productPrice,
      productLoyaltyPrice,
      showLoyaltyPrice,
      productImages,
      productDataIsLoading,
      addProductToCart,
      loading,

      dimensionValues,

      relatedProducts,

      customizationOptions,
      productCanBeAdded,
      transports,
      isGiftCardProduct,
      isEmpty,
      relatedProductsLoading,
      analyticsIndex: productAnalyticsStore.productId,
      unescapedDescription,
      stringHtml
    }
  },
  head () {
    const builder = new MetaBuilder()
    const baseURL = this.storeURL + this.$route.fullPath

    const title: string = this.metaTitle ? `${this.metaTitle}` : this.$i18n.t('HOME_PAGE_TITLE') as string
    const description: string = (this.stringHtml || this.$i18n.t('HOME_PAGE_DESCRIPTION')) as string
    const image: string = this.metaProductImage as string || this.storeURL + '/logo/logo_xl.png'

    return builder
      .useTitle(title)
      .useDescription(description)
      .useRobots(true, true)
      .useName(this.productName as string)
      .useImage(image as string)
      .useOpenGraph({
        title,
        description,
        type: 'product',
        url: baseURL,
        image
      }).build()
  }
})
